<script setup>
import { computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { useKbd } from 'dashboard/composables/utils/useKbd';

defineProps({
  isGeneratingContent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();
const handleCancel = () => {
  emit('cancel');
};

const shortcutKey = useKbd(['$mod', '+', 'enter']);

const acceptLabel = computed(() => {
  return `${t('GENERAL.ACCEPT')}  (${shortcutKey.value})`;
});

const handleSubmit = () => {
  emit('submit');
};
</script>

<template>
  <div
    v-show="!isGeneratingContent"
    class="copilot-reply-actions relative z-[1]"
  >
    <div class="copilot-reply-actions__hint">
      <i class="i-lucide-keyboard" aria-hidden="true" />
      <span>{{ t('CONVERSATION.REPLYBOX.JARVIS_KEYBOARD_HINT') }}</span>
    </div>
    <div class="copilot-reply-actions__buttons">
      <NextButton
        :label="t('GENERAL.DISCARD')"
        slate
        link
        class="copilot-reply-actions__discard !px-1 hover:!no-underline"
        sm
        :disabled="isGeneratingContent"
        @click="handleCancel"
      />
      <NextButton
        :label="acceptLabel"
        class="copilot-reply-actions__accept bg-n-blue-9 text-white"
        solid
        sm
        :disabled="isGeneratingContent"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<style scoped>
.copilot-reply-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.625rem 0.75rem 0.75rem;
}

.copilot-reply-actions__hint {
  display: none;
}

.copilot-reply-actions__buttons {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-left: auto;
}

@media (min-width: 769px) {
  .copilot-reply-actions {
    padding: 0.875rem 1.25rem;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
    background: #f8fafc;
  }

  .copilot-reply-actions__hint {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.5rem;
    color: #64748b;
    font-size: 0.75rem;
  }

  .copilot-reply-actions__hint i {
    width: 1rem;
    height: 1rem;
    flex: 0 0 1rem;
  }

  .copilot-reply-actions__discard {
    height: 2rem;
    padding: 0 0.75rem !important;
    border: 1px solid #cbd5e1;
    border-radius: 0.5rem;
    color: #475569;
    background: #fff;
  }

  .copilot-reply-actions__discard:hover:not(:disabled) {
    color: #0f172a;
    background: #f1f5f9;
  }

  .copilot-reply-actions__accept {
    color: #fff;
    background: #00a884;
  }

  .copilot-reply-actions__accept:hover:not(:disabled) {
    background: #008f72;
  }

  .dark .copilot-reply-actions {
    border-color: rgba(255, 255, 255, 0.08);
    background: #111827;
  }

  .dark .copilot-reply-actions__hint {
    color: #94a3b8;
  }

  .dark .copilot-reply-actions__discard {
    border-color: #475569;
    color: #cbd5e1;
    background: #1f2937;
  }
}
</style>
